import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import { Plus, Edit, Trash2, Loader2, QrCode as QrCodeIcon, Link2, MousePointerClick } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { toast } from '../../../hooks/use-toast';
import { QR_TYPES } from '../../../lib/qrEncode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeLabel = (value) => QR_TYPES.find((t) => t.value === value)?.label || value;

const QrThumbnail = ({ qr }) => {
  const ref = React.useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = '';
    const settings = qr.settings || {};
    const instance = new QRCodeStyling({
      width: 96,
      height: 96,
      data: qr.data || ' ',
      margin: 4,
      qrOptions: { errorCorrectionLevel: settings.ecc || 'M' },
      dotsOptions: { type: settings.dotStyle || 'square', color: settings.fgColor || '#000000' },
      cornersSquareOptions: { type: settings.cornerSquareStyle || 'square', color: settings.fgColor || '#000000' },
      cornersDotOptions: { type: settings.cornerDotStyle || 'square', color: settings.fgColor || '#000000' },
      backgroundOptions: { color: settings.bgColor || '#ffffff' },
    });
    instance.append(ref.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qr.id]);

  return <div ref={ref} className="w-24 h-24 flex items-center justify-center" />;
};

const QrCodesList = ({ basePath = '/admin/qr-builder' }) => {
  const [codes, setCodes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('__all');
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchData = async () => {
    try {
      const [codesRes, projectsRes] = await Promise.all([
        axios.get(`${API}/qr-codes`),
        axios.get(`${API}/qr-projects`),
      ]);
      setCodes(codesRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Could not load your QR codes.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const projectById = (id) => projects.find((p) => p.id === id);
  const visibleCodes = projectFilter === '__all'
    ? codes
    : projectFilter === '__none'
      ? codes.filter((c) => !c.project_id)
      : codes.filter((c) => c.project_id === projectFilter);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this QR code? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/qr-codes/${id}`);
      setCodes((prev) => prev.filter((c) => c.id !== id));
      toast({ title: 'Deleted', description: 'QR code removed.' });
    } catch (error) {
      toast({ title: 'Error', description: 'Could not delete this QR code.', variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF6B00]" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All QR Codes</h1>
          <p className="text-gray-500 text-sm">{visibleCodes.length} of {codes.length} saved</p>
        </div>
        <div className="flex items-center gap-3">
          {projects.length > 0 && (
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Projects</SelectItem>
                <SelectItem value="__none">No Project</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: p.color }} />
                      {p.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button asChild className="bg-[#FF6B00] hover:bg-[#e05f00]">
            <Link to={`${basePath}/new`}><Plus className="w-4 h-4 mr-2" /> Create New</Link>
          </Button>
        </div>
      </div>

      {visibleCodes.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center text-center">
            <div className="p-4 bg-orange-50 rounded-full mb-4">
              <QrCodeIcon className="w-8 h-8 text-[#FF6B00]" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No QR codes yet</h3>
            <p className="text-gray-500 text-sm mb-4">Create your first one to see it here.</p>
            <Button asChild className="bg-[#FF6B00] hover:bg-[#e05f00]">
              <Link to={`${basePath}/new`}><Plus className="w-4 h-4 mr-2" /> Create New</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCodes.map((qr) => {
            const project = qr.project_id ? projectById(qr.project_id) : null;
            return (
            <Card key={qr.id} data-testid={`qr-code-card-${qr.id}`}>
              <CardContent className="p-5 flex gap-4">
                <QrThumbnail qr={qr} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900 truncate">{qr.name}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <Badge variant="secondary">{typeLabel(qr.type)}</Badge>
                    {qr.is_dynamic && (
                      <Badge className="bg-[#FF6B00]/10 text-[#FF6B00] hover:bg-[#FF6B00]/10 border-0">
                        <Link2 className="w-3 h-3 mr-1" /> Dynamic
                      </Badge>
                    )}
                    {project && (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: project.color }} />
                        {project.name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-xs text-gray-400">
                      {new Date(qr.created_at).toLocaleDateString()}
                    </p>
                    {qr.is_dynamic && (
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <MousePointerClick className="w-3 h-3" /> {qr.clicks || 0} scan{qr.clicks === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`${basePath}/edit/${qr.id}`}><Edit className="w-3.5 h-3.5 mr-1.5" /> Edit</Link>
                    </Button>
                    <Button
                      size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(qr.id)}
                      disabled={deletingId === qr.id}
                    >
                      {deletingId === qr.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QrCodesList;
